import React, { useState } from 'react';
import { X, BookOpen, PlayCircle } from 'lucide-react';

interface BookSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onStartReading: (chapter: string, content: string) => void;
  title: string;
  chapters: Array<{
    id: string;
    title: string;
    content: string;
    wordCount: number;
  }>;
}

export const BookSelectionModal: React.FC<BookSelectionModalProps> = ({
  isOpen,
  onClose,
  onStartReading,
  title,
  chapters,
}) => {
  const [selectedChapter, setSelectedChapter] = useState<string>('');

  const handleChapterSelect = (chapterId: string, content: string) => {
    setSelectedChapter(chapterId);
    onStartReading(chapterId, content);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/50 p-4">
      <div className="relative max-w-2xl mx-auto">
        <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700">
            <h2 className="text-xl font-bold text-slate-900 dark:text-white">Select Chapter - {title}</h2>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 p-2 rounded-lg"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Chapter List */}
          <div className="max-h-96 overflow-y-auto">
            {chapters.map((chapter) => (
              <button
                key={chapter.id}
                onClick={() => handleChapterSelect(chapter.id, chapter.content)}
                className={`w-full flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors ${
                  selectedChapter === chapter.id
                    ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-600'
                    : 'hover:bg-slate-50 dark:hover:bg-slate-800'
                }`}
              >
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
                    {chapter.title}
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    {chapter.wordCount.toLocaleString()} words
                  </p>
                </div>
                <PlayCircle className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </button>
            ))}
          </div>

          {/* Footer */}
          <div className="p-4">
            <p className="text-xs text-slate-500 dark:text-slate-400 text-center">
              Select a chapter to begin reading
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};